import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import ServiceWizard from "@/pages/ServiceWizard";
import AdminDashboard from "@/pages/AdminDashboard";
import Contact from "@/pages/Contact";
import Tradelines from "@/pages/Tradelines";
import AuthCallback from "@/pages/AuthCallback";
import PublicForm from "@/pages/PublicForm";
import Portal from "@/pages/Portal";
import SetPassword from "@/pages/SetPassword";
import CommandLayout from "@/pages/command/CommandLayout";
import CommandDashboard from "@/pages/command/CommandDashboard";
import Clients from "@/pages/command/Clients";
import ClientDetail from "@/pages/command/ClientDetail";
import Companies from "@/pages/command/Companies";
import CompanyDetail from "@/pages/command/CompanyDetail";
import Tasks from "@/pages/command/Tasks";
import Documents from "@/pages/command/Documents";
import Billing from "@/pages/command/Billing";
import Letters from "@/pages/command/Letters";
import Approvals from "@/pages/command/Approvals";

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" />
  </div>
);

const Protected = ({ children, adminOnly }) => {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
};

function AppRoutes() {
  const location = useLocation();
  // Process the OAuth callback FIRST (synchronously) before any protected route runs.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/tradelines" element={<Tradelines />} />
      <Route path="/forms/:kind" element={<PublicForm />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/portal" element={<Protected><Portal /></Protected>} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/service/:serviceId" element={<Protected><ServiceWizard /></Protected>} />
      <Route path="/admin" element={<Protected adminOnly><AdminDashboard /></Protected>} />
      <Route path="/command" element={<Protected adminOnly><CommandLayout /></Protected>}>
        <Route index element={<CommandDashboard />} />
        <Route path="clients" element={<Clients />} />
        <Route path="clients/:id" element={<ClientDetail />} />
        <Route path="companies" element={<Companies />} />
        <Route path="companies/:id" element={<CompanyDetail />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="billing" element={<Billing />} />
        <Route path="letters" element={<Letters />} />
        <Route path="approvals" element={<Approvals />} />
        <Route path="documents" element={<Documents />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster theme="dark" position="top-right" richColors />
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
